import { motion, AnimatePresence } from "framer-motion";
import { X, Copy, ExternalLink, LogOut, AlertTriangle, Loader2, TrendingUp, ArrowRightLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useWallet } from "@/hooks/useWallet";
import { CHAIN_NAME, CHAIN_EXPLORER } from "@/lib/wagmi";
import {
  GLO_USD, HALO_USD, HALO_PER_GLO,
  REWARD_LIKE_GLO, REWARD_UPLOAD_GLO, REWARD_UPLOAD_HALO,
  gloToUsd, haloToUsd, fmtUsd, fmtToken, parseBalance,
} from "@/lib/rates";
import { useState } from "react";

interface WalletPanelProps {
  open: boolean;
  onClose: () => void;
}

type Tab = "wallet" | "rates";

function short(addr: string) {
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

function TokenRow({
  symbol,
  balance,
  usdValue,
  color,
  note,
}: {
  symbol: string;
  balance: string;
  usdValue: string;
  color: string;
  note: string;
}) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${color}`}>
          {symbol[0]}
        </div>
        <div>
          <p className="text-sm font-semibold">{symbol}</p>
          <p className="text-[10px] text-muted-foreground">{note}</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-sm font-mono font-semibold">{balance}</p>
        <p className="text-[10px] text-muted-foreground">{usdValue}</p>
      </div>
    </div>
  );
}

function RewardRow({ label, glo, halo, usd }: { label: string; glo?: string; halo?: string; usd: string }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0">
      <p className="text-xs text-muted-foreground flex-1 pr-2">{label}</p>
      <div className="text-right shrink-0">
        {glo  && <p className="text-xs font-mono text-primary font-semibold">{glo} GLO</p>}
        {halo && <p className="text-xs font-mono text-yellow-400 font-semibold">{halo} HALO</p>}
        <p className="text-[10px] text-muted-foreground">{usd}</p>
      </div>
    </div>
  );
}

export default function WalletPanel({ open, onClose }: WalletPanelProps) {
  const {
    address,
    isConnected,
    isConnecting,
    wrongNetwork,
    gloBalance,
    haloBalance,
    hasMetaMask: metaMaskAvailable,
    connect,
    disconnect,
    switchNetwork,
  } = useWallet();

  const [copied, setCopied] = useState(false);
  const [tab, setTab] = useState<Tab>("wallet");

  const copyAddress = () => {
    if (!address) return;
    navigator.clipboard.writeText(address);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // compute USD equivalents from raw balance strings
  const gloNum   = parseBalance(gloBalance);
  const haloNum  = parseBalance(haloBalance);
  const gloUsd   = gloToUsd(gloNum);
  const haloUsdV = haloToUsd(haloNum);
  const totalUsd = gloUsd + haloUsdV;

  const hasBalance = gloNum > 0 || haloNum > 0;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            key="panel"
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 24 }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            className="fixed top-4 right-4 z-50 w-[22rem] bg-background border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div>
                <p className="text-xs uppercase tracking-widest text-primary font-mono mb-0.5">ISM Wallet</p>
                <p className="text-xs text-muted-foreground">{CHAIN_NAME}</p>
              </div>
              <button onClick={onClose} className="text-muted-foreground hover:text-white transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Tab bar — always visible */}
            <div className="flex border-b border-white/10">
              <button
                onClick={() => setTab("wallet")}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
                  tab === "wallet" ? "text-white border-b-2 border-primary" : "text-muted-foreground hover:text-white"
                }`}
              >
                <TrendingUp className="w-3.5 h-3.5" />
                Wallet
              </button>
              <button
                onClick={() => setTab("rates")}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
                  tab === "rates" ? "text-white border-b-2 border-primary" : "text-muted-foreground hover:text-white"
                }`}
              >
                <ArrowRightLeft className="w-3.5 h-3.5" />
                Rates &amp; Rewards
              </button>
            </div>

            {/* ── RATES TAB ─────────────────────────────────────── */}
            {tab === "rates" && (
              <div className="px-5 py-4 space-y-4">
                {/* Exchange rates */}
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Exchange Rates</p>
                  <div className="bg-white/5 rounded-xl overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-bold text-primary">G</div>
                        <p className="text-sm font-semibold">1 GLO</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-mono font-bold text-green-400">{fmtUsd(GLO_USD)}</p>
                        <p className="text-[10px] text-muted-foreground">= {HALO_PER_GLO} HALO</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-yellow-500/20 flex items-center justify-center text-[10px] font-bold text-yellow-400">H</div>
                        <p className="text-sm font-semibold">1 HALO</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-mono font-bold text-green-400">{fmtUsd(HALO_USD)}</p>
                        <p className="text-[10px] text-muted-foreground">= {fmtToken(1 / HALO_PER_GLO, 4)} GLO</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Base reward schedule */}
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Base Reward Schedule</p>
                  <div className="bg-white/5 rounded-xl px-4 overflow-hidden">
                    <RewardRow
                      label="Like · Lightbulb toggle · Star rating"
                      glo={fmtToken(REWARD_LIKE_GLO, 3)}
                      usd={fmtUsd(REWARD_LIKE_GLO * GLO_USD)}
                    />
                    <RewardRow
                      label="New video / media file uploaded"
                      glo={fmtToken(REWARD_UPLOAD_GLO, 5)}
                      halo={fmtToken(REWARD_UPLOAD_HALO, 4)}
                      usd="$0.20"
                    />
                  </div>
                </div>

                <p className="text-[10px] text-muted-foreground leading-relaxed text-center pb-1">
                  Base rates apply before service or product contract adjustments.
                  Rates are set by the ISM platform owner.
                </p>
              </div>
            )}

            {/* ── WALLET TAB ────────────────────────────────────── */}
            {tab === "wallet" && (
              <>
                {isConnected && address ? (
                  <>
                    {wrongNetwork && (
                      <div className="mx-4 mt-4 flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-3 py-2.5">
                        <AlertTriangle className="w-4 h-4 text-yellow-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-yellow-400">Wrong Network</p>
                          <p className="text-[10px] text-yellow-400/70">Switch to Polygon Amoy</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs h-7 border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/10 shrink-0"
                          onClick={switchNetwork}
                        >
                          Switch
                        </Button>
                      </div>
                    )}

                    {/* Total portfolio value */}
                    {hasBalance && (
                      <div className="mx-5 mt-4 bg-white/5 rounded-xl px-4 py-3 flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">Portfolio Value</p>
                        <p className="text-lg font-bold font-mono text-green-400">{fmtUsd(totalUsd)}</p>
                      </div>
                    )}

                    {/* Address */}
                    <div className="px-5 py-4 border-b border-white/10">
                      <p className="text-xs text-muted-foreground mb-2">Connected Address</p>
                      <div className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2">
                        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse shrink-0" />
                        <p className="flex-1 font-mono text-sm truncate">{short(address)}</p>
                        <button onClick={copyAddress} className="text-muted-foreground hover:text-white transition-colors" title="Copy address">
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        <a href={`${CHAIN_EXPLORER}/address/${address}`} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-white transition-colors">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                      {copied && <p className="text-[10px] text-green-400 mt-1 text-center">Copied!</p>}
                    </div>

                    {/* Balances */}
                    <div className="px-5 py-2">
                      <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1 mt-2">Token Balances</p>
                      <TokenRow
                        symbol="GLO"
                        balance={gloBalance}
                        usdValue={gloNum > 0 ? `≈ ${fmtUsd(gloUsd)}` : `1 GLO = ${fmtUsd(GLO_USD)}`}
                        color="bg-primary/20 text-primary"
                        note="ISM Glo — Reward Token"
                      />
                      <TokenRow
                        symbol="HALO"
                        balance={haloBalance}
                        usdValue={haloNum > 0 ? `≈ ${fmtUsd(haloUsdV)}` : `1 HALO = ${fmtUsd(HALO_USD)}`}
                        color="bg-yellow-500/20 text-yellow-400"
                        note="HALO Coin — Currency"
                      />
                    </div>

                    {/* Quick rate reminder */}
                    <div className="mx-5 mb-4 mt-2 bg-primary/5 border border-primary/20 rounded-lg px-3 py-2.5">
                      <p className="text-[10px] text-muted-foreground leading-relaxed">
                        <span className="text-primary font-semibold">GLO</span> earned via likes &amp; uploads.{" "}
                        <span className="text-yellow-400 font-semibold">HALO</span> is the currency — 3 HALO = 1 GLO = {fmtUsd(GLO_USD)}.
                        {" "}See <button onClick={() => setTab("rates")} className="underline text-primary/70 hover:text-primary">Rates &amp; Rewards</button> for full schedule.
                      </p>
                    </div>

                    <div className="px-5 pb-5">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full gap-2 text-muted-foreground border-white/10 hover:border-red-500/40 hover:text-red-400"
                        onClick={() => { disconnect(); onClose(); }}
                      >
                        <LogOut className="w-4 h-4" />
                        Disconnect Wallet
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="px-5 py-6 flex flex-col gap-4">
                    <div className="text-center">
                      <div className="text-4xl mb-3">🦊</div>
                      <p className="font-semibold mb-1">Connect your wallet</p>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Connect MetaMask to earn GLO tokens, hold HALO Coins, and own your ISM digital assets on Polygon.
                      </p>
                    </div>

                    {/* Rate preview for unconnected users */}
                    <div className="bg-white/5 rounded-xl px-4 py-3 space-y-1.5">
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Earn When You</p>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Like or rate content</span>
                        <span className="font-mono text-primary">+{fmtToken(REWARD_LIKE_GLO, 3)} GLO</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Upload a video / file</span>
                        <span className="font-mono text-primary">+{fmtToken(REWARD_UPLOAD_GLO, 5)} GLO</span>
                      </div>
                      <div className="pt-1.5 border-t border-white/5 flex justify-between text-[10px]">
                        <span className="text-muted-foreground">1 GLO = 3 HALO</span>
                        <span className="text-green-400 font-mono">{fmtUsd(GLO_USD)} USD</span>
                      </div>
                    </div>

                    {metaMaskAvailable ? (
                      <Button
                        className="w-full bg-primary hover:bg-primary/80 gap-2"
                        disabled={isConnecting}
                        onClick={connect}
                      >
                        {isConnecting
                          ? <><Loader2 className="w-4 h-4 animate-spin" /> Connecting…</>
                          : <>🦊 Connect MetaMask</>}
                      </Button>
                    ) : (
                      <a
                        href="https://metamask.io/download/"
                        target="_blank"
                        rel="noreferrer"
                        className="w-full"
                      >
                        <Button className="w-full bg-primary hover:bg-primary/80 gap-2">
                          🦊 Install MetaMask
                        </Button>
                      </a>
                    )}

                    <p className="text-[10px] text-center text-muted-foreground">
                      Uses Polygon Amoy testnet · No real funds needed to start
                    </p>
                  </div>
                )}
              </>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
